import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IconDownload } from '@tabler/icons-react';
import { useApp } from '../context/AppContext';
import { useToast } from '../context/ToastContext';
import { useDisplayTimezone } from '../context/TimeModeContext';
import {
  toLocalTime,
  getMondayOfWeek,
  todayDateStringInTz,
  pairIntoSessions,
  DAY_ANCHOR_TZ,
} from '../utils/time';
import Layout from '../components/Layout';
import SelectField from '../components/SelectField';

// Notes longer than this collapse to one line by default, with a toggle to
// expand — keeps report rows compact and scannable, matching the same
// threshold used for note truncation elsewhere in the app (TeamGrid.js).
const NOTE_COLLAPSE_THRESHOLD = 140;

// Rows rendered per "page" — a wide date range / "All VAs" can produce a
// long table, so we only mount this many rows at a time and load more as
// the sentinel row scrolls into view, instead of rendering everything at
// once. CSV export is unaffected — it always uses the full `rows` array.
const PAGE_SIZE = 50;

// Flattens each selected person's sessions within [fromDate, toDate] (both
// CST day-strings, inclusive) into one row per IN/OUT entry — this is the
// single source of truth for both the on-screen table and the CSV export,
// so they can never drift apart. Sorted by employee (in Users-sheet order),
// then chronologically within each employee.
function buildReportRows(users, timelogs, tz, fromDate, toDate, selectedUuid) {
  const people = selectedUuid === 'all' ? users : users.filter((u) => u.uuid === selectedUuid);
  const rows = [];

  people.forEach((user) => {
    const ownLogs = timelogs
      .filter((l) => l.uuid === user.uuid)
      .sort((a, b) => new Date(a.logtime_utc) - new Date(b.logtime_utc));
    const sessions = pairIntoSessions(ownLogs).filter(
      (s) => s.dayKey >= fromDate && s.dayKey <= toDate
    );

    sessions.forEach((s) => {
      [s.in, s.out].forEach((entry) => {
        if (!entry) return;
        rows.push({
          uuid: user.uuid,
          employee: user.name,
          role: user.role,
          isAdmin: user.isAdmin,
          dateStr: s.dayKey,
          dateLabel: new Date(`${s.dayKey}T00:00:00.000Z`).toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            timeZone: 'UTC',
          }),
          type: entry.isIn ? 'In' : 'Out',
          time: toLocalTime(entry.logtime_utc, tz),
          note: entry.notes || '',
          isManual: Boolean(entry.is_manual),
          notesEdited: Boolean(entry.notes_edited),
        });
      });
    });
  });

  return rows;
}

function escapeCsvCell(value) {
  const str = String(value ?? '');
  if (/[",\n]/.test(str)) return '"' + str.replace(/"/g, '""') + '"';
  return str;
}

function downloadCsv(filename, rows) {
  const content = rows.map((row) => row.map(escapeCsvCell).join(',')).join('\r\n');
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function Reports() {
  const { currentUser, users, timelogs } = useApp();
  const { showError } = useToast();
  const displayTz = useDisplayTimezone(currentUser);

  const todayCst = useMemo(() => todayDateStringInTz(DAY_ANCHOR_TZ), []);
  const defaultFrom = useMemo(() => getMondayOfWeek(undefined, DAY_ANCHOR_TZ), []);

  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(todayCst);
  const [selectedUuid, setSelectedUuid] = useState('all');

  const rangeInvalid = fromDate > toDate;

  const rows = useMemo(() => {
    if (rangeInvalid) return [];
    return buildReportRows(users, timelogs, displayTz, fromDate, toDate, selectedUuid);
  }, [users, timelogs, displayTz, fromDate, toDate, selectedUuid, rangeInvalid]);

  const peopleWithNoActivity = useMemo(() => {
    if (rangeInvalid) return [];
    const people = selectedUuid === 'all' ? users : users.filter((u) => u.uuid === selectedUuid);
    const activeUuids = new Set(rows.map((r) => r.uuid));
    return people.filter((u) => !activeUuids.has(u.uuid));
  }, [users, selectedUuid, rows, rangeInvalid]);

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [fromDate, toDate, selectedUuid]);

  const visibleRows = rows.slice(0, visibleCount);
  const hasMore = visibleCount < rows.length;

  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!hasMore) return undefined;
    const el = sentinelRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((c) => Math.min(c + PAGE_SIZE, rows.length));
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, rows.length]);

  const handleToDateChange = (value) => {
    if (value > todayCst) {
      showError("Can't run a report past today (CST).");
      return;
    }
    setToDate(value);
  };

  const handleExport = () => {
    if (rangeInvalid) {
      showError('"From" date must be on or before "To" date.');
      return;
    }
    if (rows.length === 0) {
      showError('No note in this range to export.');
      return;
    }
    const csvRows = [
      ['Employee', 'Role', 'Date', 'Type', 'Time', 'Note', 'Manually Logged', 'Note Edited'],
      ...rows.map((r) => [
        r.employee,
        r.role,
        r.dateLabel,
        r.type,
        r.time,
        r.note,
        r.isManual ? 'Yes' : 'No',
        r.notesEdited ? 'Yes' : 'No',
      ]),
    ];
    downloadCsv(`trackwithtux-report_${fromDate}_to_${toDate}.csv`, csvRows);
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-6 sm:py-10">
        <h1 className="text-lg sm:text-xl font-semibold text-ink">Reports</h1>
        <p className="text-xs sm:text-sm text-muted mt-1">
          Pick a date range and a team member (or everyone) to build a detailed report.
        </p>

        <div className="bg-card border border-border rounded-2xl shadow-sm p-4 sm:p-6 mt-4">
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">From</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                max={todayCst}
                className="w-full border border-border rounded-lg px-2 py-1.5 text-sm text-ink"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">To</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => handleToDateChange(e.target.value)}
                max={todayCst}
                className="w-full border border-border rounded-lg px-2 py-1.5 text-sm text-ink"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Team member</label>
              <SelectField value={selectedUuid} onChange={(e) => setSelectedUuid(e.target.value)}>
                <option value="all">All VAs</option>
                {users.map((u) => (
                  <option key={u.uuid} value={u.uuid}>
                    {u.name} {u.isAdmin ? '(Admin)' : ''}
                  </option>
                ))}
              </SelectField>
            </div>
          </div>

          {rangeInvalid && (
            <p className="mt-3 text-xs text-error-tx">"From" date must be on or before "To" date.</p>
          )}

          <p className="mt-3 text-xs text-muted">
            {fromDate} – {toDate} (CST, day-by-day) · times shown in {displayTz}
          </p>

          <button
            onClick={handleExport}
            className="mt-4 flex items-center gap-2 px-4 py-2 rounded-xl font-medium border border-border text-ink hover:bg-card-tint transition-colors text-sm"
          >
            <IconDownload size={16} stroke={1.8} />
            Download CSV
          </button>
        </div>

        <div className="bg-card border border-border rounded-2xl shadow-sm mt-6 overflow-hidden">
          {rows.length === 0 ? (
            <p className="text-xs text-muted italic p-4 sm:p-6">No note in this range.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-light-gold text-left">
                    <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide whitespace-nowrap">Employee</th>
                    <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide whitespace-nowrap">Date</th>
                    <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide whitespace-nowrap">Type</th>
                    <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide whitespace-nowrap">Time</th>
                    <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r, i) => (
                    <tr
                      key={`${r.uuid}-${r.dateStr}-${r.type}-${i}`}
                      className="border-t border-border align-top hover:bg-card-tint transition-colors"
                    >
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <p className="font-medium text-ink">{r.employee}</p>
                        <p className="text-[10px] text-muted uppercase tracking-wide">{r.isAdmin ? 'Admin' : 'VA'}</p>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-ink">{r.dateLabel}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <span
                          className={`text-[10px] font-semibold uppercase tracking-wide ${
                            r.type === 'In' ? 'text-success-tx' : 'text-out-tx'
                          }`}
                        >
                          {r.type}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-ink">{r.time}</td>
                      <td className="px-3 py-2.5 text-ink min-w-[16rem]">
                        <NoteCell note={r.note} isManual={r.isManual} notesEdited={r.notesEdited} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {hasMore && (
                <div ref={sentinelRef} className="text-center py-3">
                  <span className="text-[11px] text-muted italic">Loading more…</span>
                </div>
              )}
            </div>
          )}
        </div>

        {rows.length > 0 && (
          <p className="text-[11px] text-muted mt-2">
            Showing {visibleRows.length} of {rows.length} entries.
          </p>
        )}

        {selectedUuid === 'all' && peopleWithNoActivity.length > 0 && (
          <p className="text-xs text-muted mt-3">
            No note in this range for: {peopleWithNoActivity.map((u) => u.name).join(', ')}.
          </p>
        )}
      </div>
    </Layout>
  );
}

// Long notes default to collapsed (one line, ellipsis) so rows stay compact
// — tap to expand in place. Short notes just render directly, no toggle
// needed. CSV export is unaffected either way; this is display-only.
function NoteCell({ note, isManual, notesEdited }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = note && note.length > NOTE_COLLAPSE_THRESHOLD;
  const tags = [isManual && 'manually logged', notesEdited && 'note edited'].filter(Boolean).join(' · ');

  if (!note) {
    return tags ? <span className="text-[10px] italic text-muted">{tags}</span> : null;
  }

  return (
    <div>
      {isLong && !expanded ? (
        <button
          onClick={() => setExpanded(true)}
          className="block w-full text-left"
        >
          <span className="truncate block max-w-md">{note}</span>
          <span className="text-[11px] font-semibold text-bronze">Show more</span>
        </button>
      ) : (
        <div>
          <p className="whitespace-pre-wrap">{note}</p>
          {isLong && (
            <button
              onClick={() => setExpanded(false)}
              className="text-[11px] font-semibold text-bronze mt-0.5"
            >
              Show less
            </button>
          )}
        </div>
      )}
      {tags && <span className="block text-[10px] italic text-muted mt-0.5">{tags}</span>}
    </div>
  );
}
